'use client';

import { getAffiliateOffers } from '@/lib/data/affiliate-offers';
import { trackAffiliateClick } from '@/lib/analytics';

interface AffiliateRecommendationProps {
  /** Tool's category slug (security, developer, network, ...). */
  category: string;
  /** Tool slug — used for a more specific offer match before falling back to category. */
  toolSlug: string;
  className?: string;
}

/**
 * Renders a small "recommended" block after the tool result, driven by
 * lib/data/affiliate-offers.ts. Renders nothing when no activated offer matches
 * the tool/category (offers are gated on having a real affiliateLink), so it's
 * invisible until a program is approved and its link is pasted into the config.
 *
 * Compliance: every link is rel="sponsored nofollow" + target="_blank", with a
 * visible FTC disclosure above the offers (required when affiliate links are present).
 */
export default function AffiliateRecommendation({
  category,
  toolSlug,
  className = '',
}: AffiliateRecommendationProps) {
  const offers = getAffiliateOffers(toolSlug, category);
  if (offers.length === 0) return null;

  return (
    <section className={`px-4 py-8 ${className}`} aria-label="Recommended tools">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-baseline justify-between gap-3 mb-1">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Recommended
          </h2>
          {/* FTC disclosure — must be visible and near the links (16 CFR Part 255). */}
          <p className="text-xs text-gray-400 dark:text-gray-500">
            Affiliate links — we may earn a commission at no extra cost to you.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {offers.map((offer) => (
            <div
              key={offer.slug}
              className="flex flex-col bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5"
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="font-semibold text-gray-900 dark:text-gray-100">
                  {offer.name}
                </span>
                {offer.badge && (
                  <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#2cc3d1]/15 text-[#1b8e99] dark:text-[#5fd8e3]">
                    {offer.badge}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300 flex-1">
                {offer.blurb}
              </p>
              <a
                href={offer.affiliateLink}
                target="_blank"
                rel="noopener sponsored nofollow"
                onClick={() =>
                  trackAffiliateClick(offer.name, toolSlug, offer.affiliateLink)
                }
                className="mt-4 inline-block self-start px-5 py-2.5 rounded-md font-semibold bg-[#2813e8] text-white hover:bg-[#1f0fc0] transition-all active:scale-95"
              >
                {offer.cta}
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
